/**
 * Segredos do webchat.
 *
 * O valor entra e nunca mais sai: é gravado numa coleção que nenhum cliente lê — nem este painel.
 * A tela reflete isso, mostrando só o nome e a forma de referenciá-lo (`{{secret:nome}}`).
 *
 * Os cadastrados vêm **antes** do formulário de cadastro, invertendo a ordem antiga. Quem abre esta
 * tela quase sempre quer conferir o que existe; quem vem cadastrar sabe que precisa rolar. A lista
 * primeiro também responde na hora a pergunta que o webhook de backend faz do outro lado do painel:
 * "qual era mesmo o nome do token?".
 */

import { useState } from 'react';
import { useEditor } from '../../store/editor.js';
import { Button, EmptyState, Field, IconButton, Input, Notice, Section } from '../ui.js';
import { IconeLixeira, IconeSegredos } from '../icons.js';
import { SeloPropagacao } from './Propagacao.js';

export function SecretsSection() {
  const secretNames = useEditor((state) => state.secretNames);
  const upsertSecret = useEditor((state) => state.upsertSecret);
  const deleteSecret = useEditor((state) => state.deleteSecret);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    const normalized = name.trim().toLowerCase();
    if (!/^[a-z0-9_-]{1,64}$/.test(normalized)) {
      setError('Nome: minúsculas, números, _ e - (até 64 caracteres).');
      return;
    }
    if (!value) {
      setError('Informe o valor do segredo.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upsertSecret(normalized, value);
      setName('');
      setValue('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Section
        icon={IconeSegredos}
        title="Segredos cadastrados"
        description="Use no webhook de backend como {{secret:nome}}. O valor nunca volta para o painel."
        action={<SeloPropagacao versionado={false} />}
        padded={secretNames.length === 0}
      >
        {secretNames.length === 0 ? (
          <EmptyState
            icon={IconeSegredos}
            title="Nenhum segredo cadastrado"
            description="Token de CRM, chave de API, o que o webhook de backend precisar mandar num header. Cadastre abaixo."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {secretNames.map((secretName) => (
              <li key={secretName} className="flex items-center justify-between gap-3 px-4 py-3">
                <code className="min-w-0 truncate text-sm text-slate-800">{`{{secret:${secretName}}}`}</code>
                <IconButton
                  icon={IconeLixeira}
                  label={`Remover o segredo ${secretName}`}
                  variant="danger"
                  onClick={() => void deleteSecret(secretName)}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Cadastrar um segredo">
        <Notice tone="info" title="Segredos ficam só no servidor">
          <p>
            O valor é gravado numa coleção que nenhum cliente lê — nem este painel. Depois de salvar,
            você vê apenas o nome. Para trocar o valor, salve de novo com o mesmo nome.
          </p>
        </Notice>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" error={error}>
            <Input value={name} placeholder="crm_token" onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Valor">
            <Input
              type="password"
              value={value}
              placeholder="cole o token aqui"
              autoComplete="off"
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>
        </div>

        <Button variant="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Salvando…' : 'Salvar segredo'}
        </Button>
      </Section>
    </div>
  );
}
